import React, { useState } from 'react';
import {
  StagedFilterFieldDef,
  StagedFilterOption,
  StagedFilterValue,
  stagedFilterValueLabel,
} from '../../utils/stagedFilterState';
import { FilterPill as UiFilterPill } from '../ui/FilterPill';
import FilterValueMenu from './FilterValueMenu';

export interface FilterPillProps {
  field: StagedFilterFieldDef;
  options: StagedFilterOption[];
  value: StagedFilterValue;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onChange: (value: StagedFilterValue) => void;
  onRemove: () => void;
  counts?: Record<string, number>;
}

/**
 * One applied filter: label + current value opens the value menu, the trailing
 * × removes the filter entirely. Only filters the user actually added are in
 * the bar, so its length reads as query complexity rather than schema size.
 * The pill itself is the shared `components/ui/FilterPill`; this wrapper only
 * knows the staged field types and value menu.
 */
export default function FilterPill({
  field,
  options,
  value,
  open,
  onOpen,
  onClose,
  onChange,
  onRemove,
  counts,
}: FilterPillProps) {
  // Callback ref (not useRef): the menu can be opened by the Add-filter flow on
  // the same render the pill mounts, and the popover needs a settled anchor.
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const valueLabel = stagedFilterValueLabel(field, value);

  return (
    <>
      <UiFilterPill
        label={field.label}
        valueLabel={valueLabel}
        open={open}
        onOpen={onOpen}
        trailing={{
          onClick: onRemove,
          title: `Remove ${field.label} filter`,
          ariaLabel: `Remove ${field.label} filter`,
        }}
        anchorRef={setAnchorEl}
      />
      <FilterValueMenu
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        field={field}
        options={options}
        value={value}
        onChange={onChange}
        counts={counts}
      />
    </>
  );
}
